import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useTranslation } from 'next-i18next';
import { memo, useRef, useState } from 'react';
import { AmadeusRatingSection, AmadeusRatingSectionDetailsView } from '../../components/HotelDetailsOverlay/components/AmadeusRatingSection/index.js';
import { AttributesSection } from '../../components/HotelDetailsOverlay/components/AttributesSection/index.js';
import { DescriptionSection } from '../../components/HotelDetailsOverlay/components/DescriptionSection/index.js';
import { MapSection } from '../../components/HotelDetailsOverlay/components/MapSection/index.js';
import { RatingSection } from '../../components/HotelDetailsOverlay/components/RatingSection/index.js';
import { Section } from '../../components/HotelDetailsOverlay/components/Section/index.js';
import { config as defaultConfig } from '../../components/HotelDetailsOverlay/config.js';
import { ConfigProvider } from '../../components/HotelDetailsOverlay/context/Config.js';
import { SECTION_AMADEUS_RATING, SECTION_ATTRIBUTES, SECTION_DESCRIPTION, SECTION_MAP, SECTION_RATING } from '../../components/HotelDetailsOverlay/models.js';
import { TabbedContentLayer, TabPanelOverlay } from '../../primitives/ContentLayer/index.js';
import { HOTEL_DETAILS_QA_ID, qaId } from '../../utils/qaId.js';
const MemoizedDescriptionSection = memo(DescriptionSection);
const MemoizedAttributesSection = memo(AttributesSection);
const MemoizedAmadeusRatingSection = memo(AmadeusRatingSection);
const MemoizedRatingSection = memo(RatingSection);
const MemoizedMapSection = memo(MapSection);
const SectionComponents = {
  [SECTION_DESCRIPTION]: MemoizedDescriptionSection,
  [SECTION_ATTRIBUTES]: MemoizedAttributesSection,
  [SECTION_AMADEUS_RATING]: MemoizedAmadeusRatingSection,
  [SECTION_RATING]: MemoizedRatingSection,
  [SECTION_MAP]: MemoizedMapSection
};
export const HotelDetailsOverlay = ({
  open,
  data: dataProperty = {},
  config = defaultConfig,
  activeSection,
  controls,
  onChangeSection,
  onClose,
  ...tabbedContentLayerProps
}) => {
  const {
    t
  } = useTranslation();
  const {
    current: tabLabels
  } = useRef({
    [SECTION_DESCRIPTION]: t('hotelDetailsOverlay:descriptionSection.tabLabel'),
    [SECTION_ATTRIBUTES]: t('hotelDetailsOverlay:attributesSection.tabLabel'),
    [SECTION_AMADEUS_RATING]: t('hotelDetailsOverlay:ratingSection.tabLabel'),
    [SECTION_RATING]: t('hotelDetailsOverlay:ratingSection.tabLabel'),
    [SECTION_MAP]: t('hotelDetailsOverlay:mapSection.tabLabel')
  });
  const [selectedAmadeusReviewItem, setSelectedAmadeusReviewItem] = useState(null);
  const [amadeusRatingSectionDetailsViewVisible, setAmadeusRatingSectionDetailsViewVisible] = useState(false);
  const tabs = [SECTION_DESCRIPTION, SECTION_ATTRIBUTES, SECTION_AMADEUS_RATING, SECTION_RATING, SECTION_MAP].reduce((result, id) => {
    const data = dataProperty[id];
    const label = tabLabels[id];
    const active = id === activeSection;
    const Component = SectionComponents[id];
    if (!data || !Component) {
      return result;
    }
    if (id === SECTION_AMADEUS_RATING) {
      result.push({
        id,
        label,
        active,
        content: _jsx(Section, {
          active: active,
          children: _jsx(MemoizedAmadeusRatingSection, {
            data: data,
            onSelectReview: review => {
              setSelectedAmadeusReviewItem(review);
              setAmadeusRatingSectionDetailsViewVisible(true);
            }
          })
        }),
        overlay: _jsx(TabPanelOverlay, {
          visible: amadeusRatingSectionDetailsViewVisible,
          children: _jsx(AmadeusRatingSectionDetailsView, {
            review: selectedAmadeusReviewItem,
            onClickBackButton: () => {
              setAmadeusRatingSectionDetailsViewVisible(false);
            },
            onClickImage: imageIndex => {
              if (selectedAmadeusReviewItem) {
                config.amadeusRating.onClickReviewImage?.({
                  review: selectedAmadeusReviewItem,
                  imageIndex
                });
              }
            }
          })
        })
      });
    } else {
      result.push({
        id,
        label,
        active,
        content: _jsx(Section, {
          active: active,
          // google maps api should only be loaded when the tab gets actually shown
          lazy: id === SECTION_MAP,
          children: _jsx(Component, {
            data: data
          })
        })
      });
    }
    return result;
  }, []);
  if (tabs.length === 0) {
    return null;
  }
  return _jsx(ConfigProvider, {
    config: config,
    children: _jsx(TabbedContentLayer, {
      ...tabbedContentLayerProps,
      open: open,
      title: t('hotelDetailsOverlay:title'),
      closeButtonLabel: t('hotelDetailsOverlay:closeButton'),
      tabs: tabs,
      controls: controls,
      "data-testid": qaId(HOTEL_DETAILS_QA_ID),
      onChangeTab: tabId => {
        onChangeSection(tabId);
      },
      onClose: onClose
    })
  });
};